import {DateFilterItem} from '../enums';
import {DateFilterItemDefinition} from '../types';

// Možnosti pro selectbox odpovídající názvům možností v DateFilterItem
export function todayPlusWeek() {
    const from = new Date();
    const to = new Date();
    to.setDate(to.getDate() + 7);
    return {dateFrom: from, dateTo: to};
}

export function todayPlusTwoWeeks() {
    const from = new Date();
    const to = new Date();
    to.setDate(to.getDate() + 14);
    return {dateFrom: from, dateTo: to};
}

export function todayPlusMonth() {
    const from = new Date();
    const to = new Date();
    to.setMonth(to.getMonth() + 1);
    return {dateFrom: from, dateTo: to};
}

export function todayPlusFuture() {
    const from = new Date();
    return {dateFrom: from, dateTo: null};
}

export function thisWeek() {
    let from = new Date();
    from.setDate(from.getDate() - from.getDay());
    let to = new Date();
    to.setDate(to.getDate() + (6 - to.getDay()));
    return {dateFrom: from, dateTo: to};
}

export function todayPlusMinusWeek() {
    const from = new Date();
    from.setDate(from.getDate() - 7);
    from.setHours(0, 0, 0, 0);
    const to = new Date();
    to.setDate(to.getDate() + 7);
    to.setHours(23, 59, 59, 999);
    return {dateFrom: from, dateTo: to};
}

export function thisMonth() {
    let from = new Date();
    from.setDate(1);
    let to = new Date();
    to.setMonth(to.getMonth() + 1, 0);
    return {dateFrom: from, dateTo: to};
}

export function thisYear() {
    let from = new Date();
    from.setDate(1);
    from.setMonth(0);
    let to = new Date();
    to.setMonth(11, 31);
    return {dateFrom: from, dateTo: to};
}

export function nextWeek() {
    let from = new Date();
    from.setDate(from.getDate() + 7);
    from.setDate(from.getDate() - from.getDay());
    let to = new Date();
    to.setDate(to.getDate() + (6 - to.getDay()));
    to.setDate(to.getDate() + 7);
    return {dateFrom: from, dateTo: to};
}

export function nextMonth() {
    let from = new Date();
    from.setDate(1);
    from.setMonth(from.getMonth() + 1);
    let to = new Date();
    to.setMonth(to.getMonth() + 2, 0);
    return {dateFrom: from, dateTo: to};
}

export function nextYear() {
    let from = new Date();
    from.setDate(1);
    from.setMonth(0);
    from.setFullYear(from.getFullYear() + 1);
    let to = new Date();
    to.setMonth(11, 31);
    to.setFullYear(to.getFullYear() + 1);
    return {dateFrom: from, dateTo: to};
}

/**
 * Mapování předdefinovaných DateFilterItem hodnot na jejich hodnoty a názvy.
 */
const predefinedItems: Record<
    DateFilterItem,
    {name: string; value: {dateFrom: Date | null; dateTo: Date | null} | null}
> = {
    [DateFilterItem.TODAY]: {name: 'Dnes', value: {dateFrom: new Date(), dateTo: new Date()}},
    [DateFilterItem.TODAY_PLUS_WEEK]: {name: 'Dnes + týden', value: todayPlusWeek()},
    [DateFilterItem.TODAY_PLUS_TWO_WEEKS]: {name: 'Dnes + dva týdny', value: todayPlusTwoWeeks()},
    [DateFilterItem.TODAY_PLUS_MONTH]: {name: 'Dnes + měsíc', value: todayPlusMonth()},
    [DateFilterItem.TODAY_AND_FUTURE]: {name: 'Dnes a budoucnost', value: todayPlusFuture()},
    [DateFilterItem.TODAY_PLUS_MINUS_WEEK]: {name: 'Dnes +/- týden', value: todayPlusMinusWeek()},
    [DateFilterItem.THIS_WEEK]: {name: 'Tento týden', value: thisWeek()},
    [DateFilterItem.THIS_MONTH]: {name: 'Tento měsíc', value: thisMonth()},
    [DateFilterItem.THIS_YEAR]: {name: 'Tento rok', value: thisYear()},
    [DateFilterItem.NEXT_WEEK]: {name: 'Příští týden', value: nextWeek()},
    [DateFilterItem.NEXT_MONTH]: {name: 'Příští měsíc', value: nextMonth()},
    [DateFilterItem.NEXT_YEAR]: {name: 'Příští rok', value: nextYear()},
    [DateFilterItem.UNLIMITED]: {name: 'Neomezeně', value: {dateFrom: null, dateTo: null}},
    [DateFilterItem.CUSTOM_DATE]: {name: 'Vlastní datum', value: null},
    [DateFilterItem.CUSTOM_RANGE]: {name: 'Vlastní období', value: null},
};

/**
 * Vrátí seznam položek datumového filtru podle zadaných ID nebo vlastních definic.
 *
 * @param allowedItems - Pole hodnot DateFilterItem (předdefinované) nebo vlastních DateFilterItemDefinition.
 * @returns Pole objektů s id, name a value pro každou položku.
 */
export function getFilterItems(allowedItems: DateFilterItemDefinition[]): DateFilterItemDefinition[] {
    return allowedItems.map(item => {
        if (typeof item === 'string') {
            // Je to předdefinovaný DateFilterItem
            const predefined = predefinedItems[item];
            return {
                id: item,
                name: predefined.name,
                value: predefined.value,
            };
        } else {
            // Je to vlastní DateFilterItemDefinition
            return item;
        }
    });
}

/**
 * Formátuje datum na lokální řetězec (CZ formát: DD.MM.YYYY).
 */
function formatDate(date: Date | null): string {
    if (!date) return '';
    return date.toLocaleDateString('cs-CZ');
}

/**
 * Vrátí zobrazovaný název položky datumového filtru.
 * Pokud má položka hodnotu (dateFrom/dateTo), vypíše ji; jinak vrátí jen název.
 *
 * @param item - Položka datumového filtru.
 * @returns Zobrazovaný název s datem/rozsahem, nebo jen název.
 */
export function getFilterItemLabel(item: DateFilterItemDefinition): string {
    let baseName: string;
    let value: {dateFrom: Date | null; dateTo: Date | null} | null;

    if (typeof item === 'string') {
        // Je to předdefinovaný DateFilterItem
        const predefined = predefinedItems[item];
        baseName = predefined.name;
        value = predefined.value;
    } else {
        // Je to vlastní objekt
        baseName = item.name;
        value = item.value;
    }

    if (baseName || !value) return baseName;

    // Vrať formátované datum/rozsah
    const dateFrom = value.dateFrom;
    const dateTo = value.dateTo;

    if (dateFrom && dateTo) {
        return `${formatDate(dateFrom)} - ${formatDate(dateTo)}`;
    } else if (dateFrom || dateTo) {
        return formatDate(dateFrom || dateTo);
    }

    return baseName;
}
